/**
 * A vertex position must be an exact, fully-specified [x, y, z] of finite
 * numbers — not one of `src/commands.js`'s `vec3()` forgiving shapes
 * (bare-number scalar fill, `{x,y,z}`, or partial-array zero-fill). Those
 * shapes exist for hand-typed CLI convenience on whole-object transforms;
 * a single named vertex has no such "reasonable default," so a caller that
 * only supplies `[5]` almost certainly made a mistake, not an intentional
 * partial move — reject it rather than silently filling in zeros. Exported
 * so every entry point that accepts a vertex position (`addMesh`'s topology,
 * `setVertexPositions`' changes) validates it the same, strict way.
 */
export function finiteVec3(value, label) {
  if (!Array.isArray(value) || value.length !== 3 || !value.every(Number.isFinite)) {
    throw new Error(`${label} must be a finite [x, y, z]`);
  }
  return [value[0], value[1], value[2]];
}

/**
 * The numeric part of a `v<n>`/`f<n>`-shaped id, or 0 for an id in any other
 * shape (which is allowed, just not part of this counter's sequence). An id
 * whose number is past `Number.MAX_SAFE_INTEGER` is rejected rather than
 * silently rounded: `Number('9007199254740993')` is `9007199254740992`, so
 * accepting it would seed a counter that mints an id colliding with one the
 * topology already contains.
 */
export function idNumber(pattern, id, label) {
  const digits = pattern.exec(id)?.[1];
  if (digits === undefined) return 0;
  const value = Number(digits);
  if (!Number.isSafeInteger(value)) {
    throw new Error(`${label} contains id '${id}', whose number is beyond Number.MAX_SAFE_INTEGER`);
  }
  return value;
}

/**
 * An id counter must be a safe integer, so that `id++` genuinely advances
 * (past `Number.MAX_SAFE_INTEGER`, `n + 1 === n` in float64, which would mint
 * two different elements under one id) and so that the ids it produces stay in
 * the `v<n>`/`f<n>` shape a later `parseTopology` can recover the counter from.
 * A fractional counter is the subtle case worth rejecting explicitly rather
 * than rounding: it mints ids like `v5.5`, which the `^v(\d+)$` recovery above
 * does not match, so a persisted session that later arrived without an
 * explicit counter would restart numbering on top of ids already in use.
 * An absent counter is fine and simply derives from the ids present — that is
 * the documented shape for hand-authored `addMesh` topology.
 */
export function normalizedIdCounter(raw, maxNumber, label) {
  if (raw === undefined || raw === null) return maxNumber + 1;
  if (!Number.isSafeInteger(raw) || raw < 1) {
    throw new Error(`${label} must be a positive safe integer when present (got ${raw})`);
  }
  return Math.max(raw, maxNumber + 1);
}

/**
 * Throws before minting an id that would exceed `Number.MAX_SAFE_INTEGER` —
 * past that point `id++` can silently stop advancing (`2**53 + 1 === 2**53` in
 * float64 arithmetic), which would mint two topology elements under the
 * identical id instead of failing.
 *
 * Takes the **exact** number of ids each counter will mint, not a worst-case
 * upper bound. An earlier version bounded vertices at 3 per source face, which
 * is only reached when no face in the set shares an edge with another; a
 * 2-triangle quad mints 5, not 6, so a subdivision with exactly enough
 * capacity left was rejected for want of ids it was never going to ask for.
 * Both counts are known exactly before any id is assigned (the edges to split
 * and the faces to re-triangulate are both fully determined first), so there
 * is no reason to approximate.
 *
 * Phrased as `counter > MAX_SAFE_INTEGER - mintCount + 1` rather than
 * `counter + mintCount - 1 > MAX_SAFE_INTEGER`: the left-hand form is exact in
 * float64 for safe-integer inputs, while the right-hand form does the very
 * addition whose precision loss is the thing being guarded against.
 */
export function assertSafeIdCapacity(nextVertexId, vertexMintCount, nextFaceId, faceMintCount, commandName) {
  const counters = [
    ['vertex', nextVertexId, vertexMintCount],
    ['face', nextFaceId, faceMintCount],
  ];
  for (const [label, counter, mintCount] of counters) {
    if (!Number.isSafeInteger(counter) || counter < 1) {
      throw new Error(`${commandName} cannot mint ids from a ${label}-id counter that is not a positive safe integer (got ${counter})`);
    }
    if (mintCount > 0 && counter > Number.MAX_SAFE_INTEGER - mintCount + 1) {
      throw new Error(
        `${commandName} would mint a ${label} id beyond Number.MAX_SAFE_INTEGER — refusing rather than risk two different elements silently sharing an id`,
      );
    }
  }
}
